import React, { useState } from 'react';
import { usePaywallStore } from '@/stores/paywallStore';
import { usePaywallCheck } from '@/hooks/usePaywallCheck';
import { BillingService } from '@/services/BillingService';
import { useCreditStore } from '@/stores/creditStore';
import { useOrgStore } from '@/stores/orgStore';
import { X } from 'lucide-react';

const billingService = new BillingService();

/**
 * Minimal modal for exhausted trial users (scenario C).
 * NOT the full PaywallModal — no plan cards, no FAQ, no pricing tables.
 * Shows plan start date and a single CTA to end the trial early.
 */
export function ExhaustedTrialModal() {
  const isModalOpen = usePaywallStore((s) => s.isModalOpen);
  const paywallVariant = usePaywallStore((s) => s.paywallVariant);
  const closePaywall = usePaywallStore((s) => s.closePaywall);
  const { currentPeriodEnd } = usePaywallCheck();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isModalOpen || paywallVariant !== 'exhausted_trial') return null;

  // Human-friendly relative date: "in 5 days (April 6)" or just "April 6"
  let formattedDate = 'soon';
  if (currentPeriodEnd) {
    const endDate = new Date(currentPeriodEnd);
    const now = new Date();
    const diffMs = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    const dateStr = endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    if (diffDays <= 0) {
      formattedDate = 'today';
    } else if (diffDays === 1) {
      formattedDate = `tomorrow (${dateStr})`;
    } else if (diffDays <= 14) {
      formattedDate = `in ${diffDays} days (${dateStr})`;
    } else {
      formattedDate = dateStr;
    }
  }

  const handleStartPlanNow = async () => {
    setLoading(true);
    setError(null);
    try {
      await billingService.endTrialNow();
      // Brief delay to let Stripe webhook grant credits
      await new Promise((r) => setTimeout(r, 2500));
      // Refresh all stores to pick up new subscription state + credits
      useCreditStore.getState().reset();
      await useCreditStore.getState().fetchBalance();
      await useOrgStore.getState().fetchOrgs();
      closePaywall();
    } catch (err: any) {
      setError(err.message || 'Failed to start plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closePaywall} />

      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 rounded-2xl overflow-hidden"
        style={{
          background: '#1d2939',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 25px 80px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)',
        }}
      >
        {/* Close button */}
        <button
          onClick={closePaywall}
          className="absolute top-5 right-5 w-8 h-8 rounded-full flex items-center justify-center text-white/30 hover:text-white/60 hover:bg-white/5 transition-all z-10"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-10 pt-10 pb-8 text-center">
          {/* Icon — simple accent circle with clock */}
          <div className="w-12 h-12 mx-auto mb-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(200,212,90,0.08)' }}>
            <svg className="w-6 h-6" style={{ color: '#c8d45a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          {/* Header */}
          <h2 className="text-xl font-serif font-bold text-white mb-4">
            You've Used All Your Trial Credits
          </h2>

          {/* Body — clean date highlight */}
          <p className="text-sm text-white/50 mb-8 leading-relaxed max-w-xs mx-auto">
            Your paid plan starts <span className="text-white/90 font-medium">{formattedDate}</span> with{' '}
            <span className="font-medium" style={{ color: '#c8d45a' }}>4,000 fresh credits</span>.
          </p>

          {/* Error */}
          {error && (
            <div className="mb-6 p-3 rounded-lg bg-error-500/10 border border-error-500/20 text-sm text-error-400">
              {error}
            </div>
          )}

          {/* Primary CTA — matching PaywallModal .paywall-cta-btn style */}
          <button
            onClick={handleStartPlanNow}
            disabled={loading}
            className="w-full py-3.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-3"
            style={{
              background: '#c8d45a',
              color: '#1d2939',
              boxShadow: '0 4px 20px rgba(200,212,90,0.3), 0 2px 8px rgba(0,0,0,0.15)',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 6px 30px rgba(200,212,90,0.45), 0 4px 12px rgba(0,0,0,0.2)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(200,212,90,0.3), 0 2px 8px rgba(0,0,0,0.15)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {loading ? 'Starting plan...' : 'Start My Plan Now'}
          </button>

          {/* Secondary CTA — ghost outline button */}
          <button
            onClick={closePaywall}
            className="w-full py-3 rounded-xl text-sm font-medium text-white/50 transition-all hover:text-white/70 hover:bg-white/[0.04]"
            style={{
              border: '1px solid rgba(255,255,255,0.08)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
            }}
          >
            Wait for plan to start
          </button>

          {/* Footer note — more separation */}
          <p className="text-xs text-white/25 mt-7 leading-relaxed">
            Starting your plan now will begin your first billing cycle immediately.
          </p>
        </div>
      </div>
    </div>
  );
}
